import React from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../components/SEOMeta";

const AboutUs: React.FC = () => {
  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "CyberAnnals",
    alternateName: [
      "cyberannals",
      "cyberannals.com",
      "cyberAnnals",
      "cyberAnnals.com",
      "Cyberannals.com",
      "Cyberannals",
      "CyberAnnals",
      "CyberAnnals.com",
    ],
    url: "https://cyberannals.com",
    logo: "https://cyberannals.com/cyberAnnals_logo.png",
    description:
      "Comprehensive historical archive of cyber attacks dedicated to documenting and preserving the chronicles of cyber attacks throughout history.",
    foundingDate: "2025",
    founder: {
      "@type": "Person",
      name: "Muhammad Hussain",
      jobTitle: "Founder",
      affiliation: [
        {
          "@type": "EducationalOrganization",
          name: "Iqra University Main Campus Karachi",
          description: "Cyber Security Student",
        },
        {
          "@type": "EducationalOrganization",
          name: "Governor Sindh Initiative for Agentic AI",
          description: "Student",
        },
      ],
      sameAs: [
        "http://www.linkedin.com/in/muhammad-hussain456",
        "https://x.com/muhammadhu7ain",
      ],
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "",
      contactType: "customer service",
      areaServed: "Worldwide",
      availableLanguage: "English",
    },
    sameAs: [
      "http://www.linkedin.com/in/muhammad-hussain456",
      "https://x.com/muhammadhu7ain",
    ],
    knowsAbout: [
      "cyber security",
      "cybersecurity history",
      "cyber attacks",
      "cyber attacks archive",
      "digital security history",
      "cyber incident database",
      "cyber threat intelligence",
      "cybersecurity research",
    ],
  };

  return (
    <>
      <SEOMeta
        title="About CyberAnnals - Cyber Security Archive"
        description="Learn about CyberAnnals, the comprehensive historical archive of cyber attacks."
        keywords="cyberannals, cyberannals.com, cyberAnnals, cyberAnnals.com, Cyberannals.com, Cyberannals, CyberAnnals, CyberAnnals.com, Muhammad Hussain, cyber security student, Iqra University, Governor Sindh Initiative"
        canonical="https://cyberannals.com/about"
        ogType="website"
        ogImage="https://cyberannals.com/cyberAnnals_logo.png"
        structuredData={structuredData}
      />

      {/* Hidden JSON-LD for structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-400 dark:to-blue-300 bg-clip-text text-transparent">
              About Us
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-amber-500 mx-auto rounded-full"></div>
          </div>

          {/* Purpose Section */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
              Our Purpose
            </h2>
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-lg mb-4 leading-relaxed">
                CyberAnnals serves as a comprehensive historical archive
                dedicated to documenting and preserving the chronicles of cyber
                attacks throughout history. Our mission is to provide
                researchers, cybersecurity professionals, students, and the
                general public with an accessible, accurate, and detailed
                repository of cyber incidents that have shaped our digital
                landscape.
              </p>
              <p className="text-lg mb-4 leading-relaxed">
                Founded in December 2025, CyberAnnals aims to bridge the gap
                between historical cybersecurity events and contemporary
                understanding. By cataloging decades of cyber attacks, we offer
                insights into evolving threats, attack methodologies, and the
                lessons learned from each incident.
              </p>
              <p className="text-lg leading-relaxed">
                Our platform combines rigorous content curation with
                user-friendly navigation, enabling visitors to explore cyber
                events by decade, year, or specific incidents. We believe that
                understanding the past is crucial for securing our digital
                future.
              </p>
            </div>
          </section>

          {/* Founder & CEO Section */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold mb-8 text-blue-600 dark:text-blue-400">
              Founder
            </h2>

            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="flex-shrink-0">
                <div className="w-56 h-56 rounded-full overflow-hidden border-4 border-blue-500/20 dark:border-blue-400/20 shadow-xl">
                  <img
                    src="/My_Pic.jpg"
                    alt="Muhammad Hussain, Founder"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-grow">
                <h3 className="text-3xl font-bold mb-4">Muhammad Hussain</h3>
                <div className="space-y-3 mb-6">
                  <p className="text-lg font-medium text-slate-700 dark:text-slate-300">
                    Cyber Security Student at Iqra University Main Campus
                    Karachi
                  </p>
                  <p className="text-lg font-medium text-slate-700 dark:text-slate-300">
                    Student of Governor Sindh Initiative for Agentic AI
                  </p>
                </div>

                <p className="text-base leading-relaxed mb-6">
                  Muhammad Hussain is the visionary founder behind CyberAnnals,
                  bringing together his passion for cybersecurity and historical
                  documentation. As a dedicated student of cyber security at
                  Iqra University, he has developed a deep understanding of both
                  the technical aspects of cybersecurity and the importance of
                  preserving digital history.
                </p>

                <p className="text-base leading-relaxed">
                  His involvement in the Governor Sindh Initiative for Agentic
                  AI demonstrates his commitment to advancing technology and
                  education in Pakistan. Muhammad's unique perspective combines
                  rigorous content development with practical application,
                  making CyberAnnals a valuable resource for the global
                  cybersecurity community.
                </p>
              </div>
            </div>
          </section>


          {/* Navigation Links */}
          <div className="flex flex-wrap justify-center gap-4 pt-8 border-t border-slate-200 dark:border-slate-700">
            <Link
              to="/blog"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm"
            >
              Explore Blog
            </Link>
            <Link
              to="/toolkit"
              className="px-6 py-3 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition-colors font-medium text-sm"
            >
              Security Toolkit
            </Link>
            <Link
              to="/decades"
              className="px-6 py-3 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition-colors font-medium text-sm"
            >
              Browse History
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutUs;